import { AlertDialog } from '@components/ui/AlertDialog/AlertDialog';
import { SNIPPET_CATEGORY_KEYS } from '@app/constants/snippetCategoryKeys';
import {
  AuditPolicySnippetMerged,
  useAuditPolicySnippetsQuery,
  useBatchTriggerPendingSnippetsMutation,
  useDeleteAuditPolicySnippetMutation,
  useRegenerateAuditPolicySnippetMutation,
  useToggleAuditPolicySnippetApprovalMutation,
  useUpdateAuditPolicySnippetMutation,
} from '@dieterApi/snippet';
import { Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Switch,
  TextField,
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';

/**
 * Fallback parser for legacy AuditPolicySnippet rows that don't yet have the
 * dedicated `category` / `serviceName` columns populated. New rows fill those
 * columns directly via the backend mutation.
 *
 * Example: "Elopage (Ablefy) (AFFILIATE)" -> { serviceName: "Elopage (Ablefy)", categoryKey: "AFFILIATE" }
 */
function parseDescriptionFallback(description: string): { serviceName: string; categoryKey: string } {
  const match = description.match(/^(.*)\s+\(([A-Z_]+)\)$/);
  if (match) {
    return {
      serviceName: match[1].trim(),
      categoryKey: match[2].trim(),
    };
  }
  return {
    serviceName: description,
    categoryKey: '',
  };
}

function snippetServiceName(snippet: AuditPolicySnippetMerged): string {
  return snippet.serviceName ?? parseDescriptionFallback(snippet.description).serviceName;
}

function snippetCategoryKey(snippet: AuditPolicySnippetMerged): string {
  return snippet.category ?? parseDescriptionFallback(snippet.description).categoryKey;
}

export function AdminSnippets() {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'cached'>('all');
  const [sortBy, setSortBy] = useState<
    'status' | 'serviceName' | 'categoryKey' | 'updatedAt' | 'createdAt' | 'description'
  >('serviceName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<AuditPolicySnippetMerged | null>(null);
  const [editedServiceName, setEditedServiceName] = useState('');
  const [editedCategoryKey, setEditedCategoryKey] = useState('');
  const [editedSnippetText, setEditedSnippetText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snippetToDelete, setSnippetToDelete] = useState<AuditPolicySnippetMerged | null>(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [snippetToRegenerate, setSnippetToRegenerate] = useState<AuditPolicySnippetMerged | null>(null);
  const [batchTriggerDialogOpen, setBatchTriggerDialogOpen] = useState(false);

  const { data, loading, refetch } = useAuditPolicySnippetsQuery({
    page: page + 1, // MUI uses 0-based, our API uses 1-based
    pageSize,
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    sortBy,
    sortOrder,
  });

  const [updateSnippet, { loading: updateLoading }] = useUpdateAuditPolicySnippetMutation();
  const [deleteSnippet, { loading: deleteLoading }] = useDeleteAuditPolicySnippetMutation();
  const [regenerateSnippet, { loading: regenerateLoading }] = useRegenerateAuditPolicySnippetMutation();
  const [batchTriggerSnippets, { loading: batchTriggerLoading }] = useBatchTriggerPendingSnippetsMutation();
  const [toggleApproval] = useToggleAuditPolicySnippetApprovalMutation();

  // Get total pending count from database (not just current page)
  const pendingCount = data?.auditPolicySnippets.totalPending ?? 0;

  const handleShowPending = useCallback(() => {
    setStatusFilter('pending');
    setPage(0);
  }, []);

  const handleClearFilter = useCallback(() => {
    setStatusFilter('all');
    setPage(0);
  }, []);

  const handleEdit = useCallback((snippet: AuditPolicySnippetMerged) => {
    setEditingSnippet(snippet);
    setEditedServiceName(snippetServiceName(snippet));
    setEditedCategoryKey(snippetCategoryKey(snippet));
    setEditedSnippetText(snippet.snippet || '');
    setEditDialogOpen(true);
  }, []);

  const handleCreateNew = useCallback(() => {
    setEditingSnippet(null);
    setEditedServiceName('');
    setEditedCategoryKey('');
    setEditedSnippetText('');
    setEditDialogOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editedServiceName.trim() || !editedCategoryKey.trim() || !editedSnippetText.trim()) {
      enqueueSnackbar('Bitte alle Felder ausfüllen', { variant: 'warning' });
      return;
    }

    try {
      await updateSnippet({
        variables: {
          id: editingSnippet?.id, // undefined for new snippets
          serviceName: editedServiceName.trim(),
          categoryKey: editedCategoryKey.trim(),
          snippet: editedSnippetText.trim(),
        },
      });
      enqueueSnackbar(editingSnippet ? 'Snippet erfolgreich aktualisiert' : 'Snippet erfolgreich erstellt', {
        variant: 'success',
      });
      setEditDialogOpen(false);
      setEditingSnippet(null);
      refetch();
    } catch (error: any) {
      enqueueSnackbar(`Fehler beim Speichern: ${error.message}`, { variant: 'error' });
    }
  }, [
    editingSnippet,
    editedServiceName,
    editedCategoryKey,
    editedSnippetText,
    updateSnippet,
    enqueueSnackbar,
    refetch,
  ]);

  const handleDeleteClick = useCallback((snippet: AuditPolicySnippetMerged) => {
    setSnippetToDelete(snippet);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!snippetToDelete) return;

    try {
      await deleteSnippet({
        variables: { description: snippetToDelete.description },
      });
      enqueueSnackbar('Snippet erfolgreich gelöscht', { variant: 'success' });
      refetch();
    } catch (error: any) {
      enqueueSnackbar(`Fehler beim Löschen: ${error.message}`, { variant: 'error' });
    }
  }, [snippetToDelete, deleteSnippet, enqueueSnackbar, refetch]);

  const handleRegenerateClick = useCallback((snippet: AuditPolicySnippetMerged) => {
    setSnippetToRegenerate(snippet);
    setRegenerateDialogOpen(true);
  }, []);

  const handleBatchTriggerClick = useCallback(() => {
    setBatchTriggerDialogOpen(true);
  }, []);

  const handleToggleApproval = useCallback(
    async (snippet: AuditPolicySnippetMerged) => {
      try {
        await toggleApproval({
          variables: { id: snippet.id, approved: !snippet.approved },
        });
        refetch();
      } catch (error: any) {
        enqueueSnackbar(`Fehler beim Ändern des Genehmigungsstatus: ${error.message}`, { variant: 'error' });
      }
    },
    [toggleApproval, refetch, enqueueSnackbar]
  );

  const handleBatchTriggerConfirm = useCallback(async () => {
    try {
      const result = await batchTriggerSnippets();
      enqueueSnackbar(result.data?.batchTriggerPendingSnippets || 'Batch-Generierung gestartet', {
        variant: 'info',
      });
      setTimeout(() => refetch(), 1500);
    } catch (error: any) {
      enqueueSnackbar(`Fehler bei der Batch-Generierung: ${error.message}`, { variant: 'error' });
    }
  }, [batchTriggerSnippets, enqueueSnackbar, refetch]);

  const handleRegenerateConfirm = useCallback(async () => {
    if (!snippetToRegenerate) return;

    try {
      const result = await regenerateSnippet({
        variables: { description: snippetToRegenerate.description },
      });
      enqueueSnackbar(result.data?.regenerateAuditPolicySnippet || 'Snippet wird neu generiert', {
        variant: 'info',
      });
      // Wait a bit before refetching to allow background generation to start
      setTimeout(() => refetch(), 1000);
    } catch (error: any) {
      enqueueSnackbar(`Fehler beim Regenerieren: ${error.message}`, { variant: 'error' });
    }
  }, [snippetToRegenerate, regenerateSnippet, enqueueSnackbar, refetch]);

  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: 'serviceName',
        label: 'Service Name',
        options: {
          filter: false,
          sort: true,
        },
      },
      {
        name: 'categoryKey',
        label: 'Category Key',
        options: {
          filter: false,
          sort: true,
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          filter: true,
          sort: true,
          customBodyRender: (value: string) => {
            const statusConfig = {
              cached: { label: 'Cached', color: 'success' as const },
              NOT_STARTED: { label: 'Not Started', color: 'default' as const },
              GENERATING: { label: 'Generating', color: 'info' as const },
              FAILED: { label: 'Failed', color: 'error' as const },
            };
            const config = statusConfig[value as keyof typeof statusConfig] || {
              label: value,
              color: 'default' as const,
            };
            return <Chip label={config.label} color={config.color} size="small" />;
          },
        },
      },
      {
        name: 'approved',
        label: 'Freigegeben',
        options: {
          filter: true,
          sort: true,
          customBodyRender: (_: any, tableMeta: any) => {
            const snippet = data?.auditPolicySnippets.snippets[tableMeta.rowIndex];
            if (!snippet || snippet.status !== 'cached') return null;
            return (
              <Switch
                checked={snippet.approved}
                onChange={() => handleToggleApproval(snippet)}
                size="small"
                color="success"
              />
            );
          },
        },
      },
      {
        name: 'snippet',
        label: 'Snippet',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: string | null) => (
            <div
              style={{
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value || <em style={{ color: '#999' }}>Noch nicht generiert</em>}
            </div>
          ),
        },
      },
      {
        name: 'updatedAt',
        label: 'Zuletzt aktualisiert',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: string) => new Date(value).toLocaleString('de-DE'),
        },
      },
      {
        name: 'createdAt',
        label: 'Erstellt am',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: string) => new Date(value).toLocaleString('de-DE'),
        },
      },
      {
        name: 'actions',
        label: 'Aktionen',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_: any, tableMeta: any) => {
            const snippet = data?.auditPolicySnippets.snippets[tableMeta.rowIndex];
            if (!snippet) return null;

            return (
              <div style={{ display: 'flex', gap: '8px' }}>
                <IconButton size="small" onClick={() => handleEdit(snippet)} title="Bearbeiten">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleRegenerateClick(snippet)}
                  title="Neu generieren"
                  disabled={regenerateLoading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteClick(snippet)} title="Löschen">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            );
          },
        },
      },
    ],
    [data, handleEdit, handleRegenerateClick, handleDeleteClick, handleToggleApproval, regenerateLoading]
  );

  const options: MUIDataTableOptions = useMemo(
    () => ({
      filterType: 'checkbox',
      responsive: 'standard',
      selectableRows: 'none',
      download: false,
      print: false,
      serverSide: true,
      count: data?.auditPolicySnippets.total || 0,
      page,
      rowsPerPage: pageSize,
      rowsPerPageOptions: [10, 25, 50, 100],
      sortOrder: {
        name: sortBy,
        direction: sortOrder,
      },
      onTableChange: (action: string, tableState: any) => {
        switch (action) {
          case 'changePage':
            setPage(tableState.page);
            break;
          case 'changeRowsPerPage':
            setPageSize(tableState.rowsPerPage);
            setPage(0);
            break;
          case 'search':
            setSearch(tableState.searchText || '');
            setPage(0);
            break;
          case 'sort':
            if (tableState.sortOrder && tableState.sortOrder.name) {
              setSortBy(tableState.sortOrder.name as any);
              setSortOrder(tableState.sortOrder.direction);
              setPage(0);
            }
            break;
        }
      },
      textLabels: {
        body: {
          noMatch: loading ? 'Laden...' : 'Keine Einträge gefunden',
        },
        pagination: {
          rowsPerPage: 'Zeilen pro Seite:',
          displayRows: 'von',
        },
        toolbar: {
          search: 'Suche',
          downloadCsv: 'CSV herunterladen',
          print: 'Drucken',
          viewColumns: 'Spalten anzeigen',
          filterTable: 'Tabelle filtern',
        },
        filter: {
          all: 'Alle',
          title: 'FILTER',
          reset: 'ZURÜCKSETZEN',
        },
      },
      customToolbar: () => (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Button variant="contained" color="primary" onClick={handleCreateNew}>
            Manuell hinzufügen
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBatchTriggerClick}
            disabled={batchTriggerLoading || pendingCount === 0}
          >
            {batchTriggerLoading ? 'Wird ausgelöst...' : `Alle ausstehenden generieren (${pendingCount})`}
          </Button>
        </div>
      ),
    }),
    [
      data,
      page,
      pageSize,
      loading,
      sortBy,
      sortOrder,
      handleCreateNew,
      handleBatchTriggerClick,
      batchTriggerLoading,
      pendingCount,
    ]
  );

  const tableData = useMemo(() => {
    return (data?.auditPolicySnippets.snippets || []).map((snippet) => {
      return {
        serviceName: snippetServiceName(snippet),
        categoryKey: snippetCategoryKey(snippet),
        status: snippet.status,
        approved: snippet.approved,
        snippet: snippet.snippet,
        updatedAt: snippet.updatedAt,
        createdAt: snippet.createdAt,
        actions: null, // Will be rendered by customBodyRender
      };
    });
  }, [data]);

  return (
    <div>
      {pendingCount > 0 && (
        <Alert
          severity="warning"
          sx={{ marginBottom: 2 }}
          action={
            statusFilter === 'pending' ? (
              <Button color="inherit" size="small" onClick={handleClearFilter}>
                Filter entfernen
              </Button>
            ) : null
          }
        >
          Es gibt {pendingCount} ausstehende Snippet{pendingCount !== 1 ? 's' : ''}.{' '}
          {statusFilter !== 'pending' && (
            <Link
              component="button"
              variant="body2"
              onClick={handleShowPending}
              sx={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              Nur ausstehende anzeigen
            </Link>
          )}
        </Alert>
      )}
      {statusFilter !== 'all' && (
        <Alert severity="info" sx={{ marginBottom: 2 }}>
          Filter aktiv: Nur <strong>{statusFilter === 'pending' ? 'ausstehende' : 'gecachte'}</strong> Snippets werden
          angezeigt.{' '}
          <Button color="inherit" size="small" onClick={handleClearFilter} variant="outlined">
            Filter zurücksetzen
          </Button>
        </Alert>
      )}
      <MUIDataTable title="" data={tableData} columns={columns} options={options} />

      {/* Edit/Create Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '800px' } }}
      >
        <DialogTitle>
          {editingSnippet ? `Snippet bearbeiten: ${editingSnippet.description}` : 'Neuen Snippet erstellen'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
          {editingSnippet && (
            <div style={{ marginBottom: '8px', marginTop: '8px' }}>
              <strong>Status:</strong>{' '}
              {(() => {
                const statusConfig = {
                  cached: { label: 'Cached', color: 'success' as const },
                  NOT_STARTED: { label: 'Not Started', color: 'default' as const },
                  GENERATING: { label: 'Generating', color: 'info' as const },
                  FAILED: { label: 'Failed', color: 'error' as const },
                };
                const config = statusConfig[editingSnippet?.status as keyof typeof statusConfig] || {
                  label: editingSnippet?.status || 'Unknown',
                  color: 'default' as const,
                };
                return <Chip label={config.label} color={config.color} size="small" />;
              })()}
            </div>
          )}
          <TextField
            fullWidth
            value={editedServiceName}
            onChange={(e) => setEditedServiceName(e.target.value)}
            label="Service Name"
            variant="outlined"
            required
          />
          <Autocomplete
            fullWidth
            value={editedCategoryKey || null}
            onChange={(_, newValue) => setEditedCategoryKey(newValue || '')}
            options={SNIPPET_CATEGORY_KEYS as readonly string[]}
            renderInput={(params) => <TextField {...params} label="Category Key" variant="outlined" required />}
            freeSolo={false}
          />
          <TextField
            fullWidth
            multiline
            value={editedSnippetText}
            onChange={(e) => setEditedSnippetText(e.target.value)}
            label="Snippet Text"
            variant="outlined"
            sx={{ flex: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updateLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="primary"
            disabled={
              updateLoading || !editedServiceName.trim() || !editedCategoryKey.trim() || !editedSnippetText.trim()
            }
          >
            {updateLoading ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        title="Snippet löschen"
        message={`Möchten Sie den Snippet "${snippetToDelete?.description}" wirklich löschen?`}
        onAccept={handleDeleteConfirm}
        acceptText="Löschen"
      />

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog
        open={regenerateDialogOpen}
        setOpen={setRegenerateDialogOpen}
        title="Snippet neu generieren"
        message={`Möchten Sie den Snippet "${snippetToRegenerate?.description}" wirklich neu generieren lassen? Der vorhandene Snippet wird gelöscht.`}
        onAccept={handleRegenerateConfirm}
        acceptText="Neu generieren"
      />

      {/* Batch Trigger Confirmation Dialog */}
      <AlertDialog
        open={batchTriggerDialogOpen}
        setOpen={setBatchTriggerDialogOpen}
        title="Alle ausstehenden Snippets generieren"
        message={`Möchten Sie alle ${pendingCount} ausstehenden Snippet(s) zur Generierung auslösen? Bereits laufende Generierungen werden nicht erneut angestoßen.`}
        onAccept={handleBatchTriggerConfirm}
        acceptText="Alle generieren"
      />
    </div>
  );
}
